import { useEffect, useState } from "react";
import { Container, Row, Col, Breadcrumb, Card, Badge, Spinner, Alert, Modal, Form } from "react-bootstrap";
import { Link } from "react-router-dom";
import { HomeOutlined, StarOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { Rate, Tag, Avatar, notification } from "antd";
import api from "@/api/api";
import formatDate from "@/utils/formatDate";

const TaskReviewPage = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 0, review_text: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchUnreviewedTasks();
  }, []);

  const fetchUnreviewedTasks = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/tasks/unreviewed/list");
      if (response.data.success) {
        setTasks(response.data.data);
      } else {
        setError(response.data.message || "Ma'lumotlarni yuklashda xatolik");
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Ma'lumotlarni yuklashda xatolik");
    } finally {
      setLoading(false);
    }
  };

  const handleReviewClick = (task) => {
    setSelectedTask(task);
    setReviewForm({ rating: 0, review_text: "" });
    setShowReviewModal(true);
  };

  const handleCloseModal = () => {
    setShowReviewModal(false);
    setSelectedTask(null);
    setReviewForm({ rating: 0, review_text: "" });
  };

  const handleSubmitReview = async () => {
    if (!selectedTask) return;

    setSubmitting(true);
    try {
      const response = await api.post(`/tasks/${selectedTask.id}/review`, reviewForm);
      if (response.data.success) {
        // Success - task ro'yxatidan olib tashlash
        setTasks(tasks.filter(t => t.id !== selectedTask.id));
        handleCloseModal();

        // Success notification
        notification.success({
          message: "Baholash muvaffaqiyatli qo'shildi!",
          description: `"${selectedTask.title}" topshirig'i ${reviewForm.rating} yulduz bilan baholandi.`,
          icon: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
          duration: 4,
          placement: "topRight"
        });
      } else {
        notification.error({
          message: "Xatolik",
          description: response.data.message || "Baholashda xatolik yuz berdi",
          duration: 4,
          placement: "topRight"
        });
      }
    } catch (err) {
      notification.error({
        message: "Xatolik",
        description: err.response?.data?.message || err.message || "Baholashda xatolik yuz berdi",
        duration: 4,
        placement: "topRight"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      approved: "success",
      rejected: "danger",
      submitted: "warning",
      in_progress: "info",
      pending: "secondary",
    };
    return colors[status] || "secondary";
  };

  const getStatusText = (status) => {
    const texts = {
      approved: "Tasdiqlangan",
      rejected: "Rad etilgan",
      submitted: "Topshirilgan",
      in_progress: "Jarayonda",
      pending: "Kutilmoqda",
    };
    return texts[status] || status;
  };

  if (loading) {
    return (
      <Container fluid className="p-4 d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" variant="primary" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger">
          <Alert.Heading>Xatolik</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      {/* Breadcrumb */}
      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/" }}>
          <HomeOutlined />
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Baholash uchun topshiriqlar</Breadcrumb.Item>
      </Breadcrumb>

      <div className="d-flex justify-content-between align-items-center mb-4">
        {/* <h2>Baholash uchun topshiriqlar</h2> */}
        <Badge bg="primary" pill style={{ fontSize: "1rem", padding: "8px 16px" }}>
         Jami: {tasks.length} ta
        </Badge>
      </div>

      {tasks.length === 0 ? (
        <Card>
          <Card.Body className="text-center py-5">
            <StarOutlined style={{ fontSize: "3rem", color: "#ccc", marginBottom: "1rem" }} />
            <h5 className="text-muted">Baholash uchun topshiriq yo'q</h5>
            <p className="text-muted">Barcha topshiriqlar allaqachon baholangan</p>
          </Card.Body>
        </Card>
      ) : (
        <Row className="g-3">
          {tasks.map((task) => (
            <Col key={task.id} xs={12} md={6} lg={4}>
              <Card className="h-100 shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <Badge bg={getStatusColor(task.status)}>
                      {getStatusText(task.status)}
                    </Badge>
                    {task.total_reviews > 0 && (
                      <div className="text-muted" style={{ fontSize: "0.85rem" }}>
                        <StarOutlined style={{ color: "#faad14" }} /> {task.avg_rating?.toFixed(1) || "N/A"} ({task.total_reviews})
                      </div>
                    )}
                  </div>

                  <h5 className="mb-2">{task.title}</h5>

                  <p className="text-muted mb-3" style={{
                    fontSize: "0.9rem",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden"
                  }}>
                    {task.description || "Tavsif yo'q"}
                  </p>

                  {task.assigned_user && (
                    <div className="d-flex align-items-center mb-3">
                      <Avatar
                        src={task.assigned_user.picture}
                        size={32}
                        style={{ marginRight: "8px" }}
                      >
                        {task.assigned_user.name?.charAt(0)}
                      </Avatar>
                      <div>
                        <div style={{ fontSize: "0.85rem", fontWeight: 500 }}>
                          {task.assigned_user.name}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "#6c757d" }}>
                          Ijrochi
                        </div>
                      </div>
                    </div>
                  )}

                  {task.deadline && (
                    <div className="mb-3">
                      <small className="text-muted">
                        Muddat: {formatDate(task.deadline)}
                      </small>
                    </div>
                  )}

                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-primary btn-sm flex-grow-1"
                      onClick={() => handleReviewClick(task)}
                    >
                      <StarOutlined /> Baholash
                    </button>
                    <Link
                      to={`/tasks/detail/${task.id}`}
                      className="btn btn-outline-secondary btn-sm"
                    >
                      Ko'rish
                    </Link>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Review Modal */}
      <Modal show={showReviewModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Topshiriqni baholash</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedTask && (
            <>
              <div className="mb-3">
                <h6>{selectedTask.title}</h6>
                <p className="text-muted" style={{ fontSize: "0.9rem" }}>
                  {selectedTask.description || "Tavsif yo'q"}
                </p>
              </div>

              <Form.Group className="mb-3">
                <Form.Label>
                  Reyting (1-5) <span className="text-danger">*</span>
                </Form.Label>
                <div className="d-flex align-items-center gap-2">
                  <Rate
                    value={reviewForm.rating}
                    onChange={(value) => setReviewForm({ ...reviewForm, rating: value })}
                    style={{ fontSize: '28px' }}
                    allowClear={false}
                  />
                  {reviewForm.rating > 0 && (
                    <span className="text-muted">({reviewForm.rating}/5)</span>
                  )}
                </div>
                {reviewForm.rating === 0 && (
                  <small className="text-danger">Iltimos, reyting tanlang</small>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Izoh (ixtiyoriy)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  placeholder="Topshiriq haqida fikringizni yozing..."
                  value={reviewForm.review_text}
                  onChange={(e) => setReviewForm({ ...reviewForm, review_text: e.target.value })}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary" onClick={handleCloseModal} disabled={submitting}>
            Bekor qilish
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmitReview}
            disabled={submitting || reviewForm.rating === 0}
          >
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Yuklanmoqda...
              </>
            ) : (
              <>
                <StarOutlined className="me-1" />
                Baholash
              </>
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default TaskReviewPage;
